// src/admin/AdminWithdrawals.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PaidIcon from "@mui/icons-material/Paid";
import api from "../utils/api";
import { API } from "../config/apiConfig";
import AdminPageWrapper from './components/AdminPageWrapper';
import ProfessionalTable from '../components/ProfessionalTable';

// ---- Endpoints (with safe fallbacks) ----
const WITHDRAWALS_ENDPOINT =
  API?.adminWithdrawals || "/api/admin/withdrawals";

// ---- Helpers ----
const pad = (n) => String(n).padStart(2, "0");
const toYMD = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};
const firstOfThisMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};
const lastOfThisMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};
const fmtMoney = (v, dp = 2) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n.toFixed(dp) : (0).toFixed(dp);
};
const csvSafe = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};
const keyOf = (r) => r.WithdrawalID ?? r.Id ?? `${r.UserID}-${r.CreatedAt}-${r.Amount}`;

// Visual chip for status
function StatusChip({ status }) {
  const s = String(status || "").toUpperCase();
  const map = {
    PENDING: "default",
    APPROVED: "info",
    REJECTED: "error",
    PAID: "success",
  };
  const label = s ? s[0] + s.slice(1).toLowerCase() : "-";
  return <Chip size="small" label={label} color={map[s] ?? "default"} />;
}

export default function AdminWithdrawals() {
  // filters
  const [start, setStart] = useState(firstOfThisMonth());
  const [end, setEnd] = useState(lastOfThisMonth());
  const [status, setStatus] = useState(""); // "", "PENDING", "APPROVED", "REJECTED", "PAID"
  const [searchTerm, setSearchTerm] = useState(""); // user/email/app/method

  // table data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based for UI
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  // ui state
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'APPROVE' | 'REJECT' | 'PAID'
  const [selectedRow, setSelectedRow] = useState(null);

  const periodLabel = useMemo(
    () => `${toYMD(start)} → ${toYMD(end)}`,
    [start, end]
  );

  useEffect(() => {
    fetchWithdrawals({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- API calls ----
  async function fetchWithdrawals({ resetPage = false } = {}) {
    setLoading(true);
    try {
      const params = {
        periodStart: toYMD(start),
        periodEnd: toYMD(end),
        status: status || undefined,
        searchTerm: searchTerm || undefined,
        page: resetPage ? 1 : page + 1, // assume backend 1-based
        pageSize: rowsPerPage,
        sortBy: "CreatedAt",
        sortDir: "DESC",
      };

      const { data } = await api.get(WITHDRAWALS_ENDPOINT, { params });
      const items =
        data?.items ??
        data?.withdrawals ??
        (Array.isArray(data) ? data : []);
      const count = data?.total ?? data?.count ?? items.length;

      setRows(items || []);
      setTotal(Number(count) || 0);
      if (resetPage) setPage(0);
      setSuccessMsg("Withdrawals loaded");
    } catch (err) {
      console.error("❌ Fetch withdrawals failed:", err);
      setErrorMsg(err?.response?.data?.error || "Failed to load withdrawals");
    } finally {
      setLoading(false);
    }
  }

  // generic status setter for row actions
  async function updateWithdrawalStatus(row, newStatus) {
    // optimistic update
    const prev = [...rows];
    setRows((rs) =>
      rs.map((r) => (keyOf(r) === keyOf(row) ? { ...r, Status: newStatus } : r))
    );

    try {
      const id = row.WithdrawalID ?? row.Id;
      // Flexible: either /:id/status or PATCH body on /:id
      try {
        await api.patch(`${WITHDRAWALS_ENDPOINT}/${id}/status`, { status: newStatus });
      } catch {
        await api.patch(`${WITHDRAWALS_ENDPOINT}/${id}`, { status: newStatus });
      }
      setSuccessMsg(`Status set to ${newStatus}`);
    } catch (err) {
      setRows(prev); // rollback
      setErrorMsg(err?.response?.data?.error || "Failed to update status");
    }
  }

  // ---- Handlers ----
  const handleThisMonth = async () => {
    setStart(firstOfThisMonth());
    setEnd(lastOfThisMonth());
    await fetchWithdrawals({ resetPage: true });
  };

  const handleStartChange = (e) => {
    const d = new Date(e.target.value);
    if (!Number.isNaN(d.getTime())) {
      setStart(d);
      if (end < d) setEnd(d);
    }
  };
  const handleEndChange = (e) => {
    const d = new Date(e.target.value);
    if (!Number.isNaN(d.getTime()) && d >= start) {
      setEnd(d);
    }
  };

  const handleChangePage = async (_e, newPage) => {
    setPage(newPage);
    await fetchWithdrawals();
  };

  const handleChangeRowsPerPage = async (e) => {
    const v = parseInt(e.target.value, 10);
    setRowsPerPage(v);
    setPage(0);
    await fetchWithdrawals({ resetPage: true });
  };

  const handleRefresh = async () => {
    await fetchWithdrawals({ resetPage: true });
  };

  const openConfirm = (row, action) => {
    setSelectedRow(row);
    setConfirmAction(action); // 'APPROVE'|'REJECT'|'PAID'
    setConfirmOpen(true);
  };

  const doConfirm = async () => {
    if (selectedRow && confirmAction) {
      const map = {
        APPROVE: "APPROVED",
        REJECT: "REJECTED",
        PAID: "PAID",
      };
      await updateWithdrawalStatus(selectedRow, map[confirmAction]);
    }
    setConfirmOpen(false);
    setSelectedRow(null);
    setConfirmAction(null);
  };

  const exportCsv = () => {
    const headers = [
      "WithdrawalID",
      "UserID",
      "UserName",
      "Email",
      "AppId",
      "AppName",
      "Amount",
      "Currency",
      "PaymentMethod",
      "AccountDetails",
      "Status",
      "CreatedAt",
      "ProcessedAt",
      "Notes",
    ];
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        [
          r.WithdrawalID ?? r.Id ?? "",
          r.UserID ?? "",
          csvSafe(r.UserName),
          csvSafe(r.Email),
          r.AppId ?? "",
          csvSafe(r.AppName),
          r.Amount ?? "",
          r.Currency ?? "",
          csvSafe(r.PaymentMethodName ?? r.PaymentMethod),
          csvSafe(r.AccountMasked ?? r.AccountDetails ?? ""),
          r.Status ?? "",
          r.CreatedAt ? new Date(r.CreatedAt).toISOString() : "",
          r.ProcessedAt ? new Date(r.ProcessedAt).toISOString() : "",
          csvSafe(r.Notes ?? ""),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `withdrawals_${toYMD(start)}_${toYMD(end)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // derived
  const pageTotal = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.Amount) || 0), 0),
    [rows]
  );

  return (
    <AdminPageWrapper 
      title="Withdrawals" 
      subtitle="Manage user withdrawal requests and payments"
    >

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.2}>
            <TextField
              label="Start"
              type="date"
              value={toYMD(start)}
              onChange={handleStartChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.2}>
            <TextField
              label="End"
              type="date"
              value={toYMD(end)}
              onChange={handleEndChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.2}>
            <FormControl fullWidth>
              <InputLabel id="status-filter">Status</InputLabel>
              <Select
                labelId="status-filter"
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={8} md={4}>
            <TextField
              label="Search (user, email, method, app)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === "Enter") fetchWithdrawals({ resetPage: true });
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4} md="auto">
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={handleRefresh} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                onClick={handleThisMonth}
              >
                This Month
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                color="secondary"
                onClick={exportCsv}
                disabled={!rows.length}
              >
                Export CSV
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Period: {periodLabel} • Page Total: {fmtMoney(pageTotal)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <ProfessionalTable
        columns={[
          {
            header: 'Date',
            accessor: 'CreatedAt',
            render: (date) => (
              <Typography variant="body2">
                {date ? new Date(date).toLocaleString() : ""}
              </Typography>
            )
          },
          {
            header: 'User',
            accessor: 'UserName',
            render: (userName, row) => {
              const user = userName || row.Email
                ? `${userName ?? ""}${row.Email ? ` (${row.Email})` : ""}`
                : row.UserID;
              return <Typography variant="body2">{user}</Typography>;
            }
          },
          {
            header: 'App',
            accessor: 'AppName',
            render: (appName, row) => (
              <Typography variant="body2">{appName ?? row.AppId ?? "-"}</Typography>
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Amount',
            accessor: 'Amount',
            render: (amount, row) => (
              <Typography variant="body2" fontWeight={600} className="amount-positive">
                {fmtMoney(amount)} {row.Currency ?? "USD"}
              </Typography>
            ),
            style: { textAlign: 'right' }
          },
          {
            header: 'Method',
            accessor: 'PaymentMethodName',
            render: (method, row) => (
              <Typography variant="body2">{method ?? row.PaymentMethod ?? "-"}</Typography>
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Account',
            accessor: 'AccountMasked',
            render: (account, row) => (
              <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {account ?? row.AccountDetails ?? "-"}
              </Typography>
            )
          },
          {
            header: 'Status',
            accessor: 'Status',
            render: (status) => <StatusChip status={status} />,
            style: { textAlign: 'center' }
          },
          {
            header: 'Actions',
            accessor: 'Status',
            render: (status, row) => (
              <Stack direction="row" spacing={0.5} justifyContent="center">
                <Tooltip title="Approve">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => openConfirm(row, "APPROVE")}
                      disabled={String(status).toUpperCase() !== "PENDING"}
                    >
                      <CheckCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reject">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => openConfirm(row, "REJECT")}
                      disabled={String(status).toUpperCase() !== "PENDING"}
                    >
                      <HighlightOffIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Mark Paid">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => openConfirm(row, "PAID")}
                      disabled={!["APPROVED"].includes(String(status).toUpperCase())}
                    >
                      <PaidIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ),
            style: { textAlign: 'center' }
          }
        ]}
        data={rows.map((row, index) => ({ ...row, id: keyOf(row) || index }))}
        loading={loading}
        emptyMessage="No withdrawals found"
      />

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={async (_e, newPage) => {
            setPage(newPage);
            await fetchWithdrawals();
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={async (e) => {
            const v = parseInt(e.target.value, 10);
            setRowsPerPage(v);
            setPage(0);
            await fetchWithdrawals({ resetPage: true });
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {confirmAction === "APPROVE" && "Approve Withdrawal"}
          {confirmAction === "REJECT" && "Reject Withdrawal"}
          {confirmAction === "PAID" && "Mark as Paid"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to{" "}
            <b>
              {confirmAction === "APPROVE" && "approve"}
              {confirmAction === "REJECT" && "reject"}
              {confirmAction === "PAID" && "mark as paid"}
            </b>{" "}
            this withdrawal request?
          </Typography>
          {selectedRow && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              User: {selectedRow.UserName ?? selectedRow.Email ?? selectedRow.UserID} • Amount:{" "}
              {fmtMoney(selectedRow.Amount)} {selectedRow.Currency ?? "USD"}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmAction === "REJECT" ? "error" : "primary"}
            onClick={doConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!errorMsg || !!successMsg}
        autoHideDuration={3500}
        onClose={() => {
          setErrorMsg("");
          setSuccessMsg("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={errorMsg ? "error" : "success"}
          onClose={() => {
            setErrorMsg("");
            setSuccessMsg("");
          }}
          sx={{ width: "100%" }}
        >
          {errorMsg || successMsg}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}
